import React, { useEffect, useState } from "react";
import axios from "axios";
import { Printer, FileSpreadsheet, UserPlus, RotateCcw } from "lucide-react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { printTableWithHeader } from "../../../utils/printUtils";
import "./UsersPage.css";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const formatTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export default function UsersPage() {

  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [serverTime, setServerTime] = useState({ fullDateTime: "" });
  const [currentUser, setCurrentUser] = useState({ fullname: "", systemRole: "" });

  /* NAVIGATE TO ADD USER PAGE */
  const handleAddUser = () => {
    navigate("/admin-panel/add-user");
  };

  /* LOAD CURRENT USER + ROLE CHECK */
  useEffect(() => {

    const user = JSON.parse(localStorage.getItem("user"));

    if (!user) {
      navigate("/login");
      return;
    }

    setCurrentUser(user);

    if (user.systemRole !== "SYSTEM ADMIN") {
      alert("Access Denied: Only System Administrators can access this page.");
      navigate("/admin-panel/dashboard");
    }

  }, [navigate]);



  /* FETCH USERS + SERVER TIME */
  useEffect(() => {

    const fetchData = async () => {
      try {

        const [usersRes, timeRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/users`),
          axios.get(`${API_BASE_URL}/time/datetime`)
        ]);

        setUsers(usersRes.data || []);

        if (timeRes.data.success) {
          setServerTime(timeRes.data);
        }

      } catch (err) {
        console.error("Error fetching users:", err);
      }
    };

    fetchData();

  }, []);



  /* UPDATE POSITION */
  const handlePositionChange = async (id, value) => {

    try {

      await axios.patch(`${API_BASE_URL}/users/position/${id}`, {
        position: value
      });

      setUsers(users.map(u =>
        u._id === id ? { ...u, position: value } : u
      ));

    } catch (err) {
      console.error(err);
    }

  };



  /* UPDATE ROLE */
  const handleRoleChange = async (id, value) => {

    try {

      await axios.patch(`${API_BASE_URL}/users/system-role/${id}`, {
        systemRole: value
      });

      setUsers(users.map(u =>
        u._id === id ? { ...u, systemRole: value } : u
      ));

    } catch (err) {
      console.error(err);
    }

  };



  /* UPDATE STATUS */
  const handleStatusChange = async (id, value) => {

    try {

      await axios.patch(`${API_BASE_URL}/users/status/${id}`, {
        status: value
      });

      setUsers(users.map(u =>
        u._id === id ? { ...u, status: value } : u
      ));

    } catch (err) {
      console.error(err);
    }

  };



  /* RESET PASSWORD */
  const handleReset = async (id) => {

    if (!window.confirm("Reset this user's password to their AlumniID?")) return;

    try {

      await axios.patch(`${API_BASE_URL}/users/reset-password/${id}`);

      alert("Password reset successfully");

    } catch (err) {
      console.error(err);
    }

  };



  /* PRINT USERS */
  const handlePrint = () => {

    printTableWithHeader({
      title: "System Users",
      summary: "List of all registered system users",

      columns: [
        { key: "sn", label: "SN" },
        { key: "alumniID", label: "Alumni ID" },
        { key: "fullname", label: "Full Name" },
        { key: "position", label: "Position" },
        { key: "role", label: "System Role" },
        { key: "status", label: "Status" },
      ],

      rows: users.map((u, index) => ({
        sn: index + 1,
        alumniID: u.alumniID,
        fullname: `${u.title || ""} ${u.firstName} ${u.lastName}`,
        position: u.position,
        role: u.systemRole,
        status: u.status
      }))
    });

  };



  /* EXPORT EXCEL */
  const exportExcel = () => {

    const data = users.map((u, index) => ({
      SN: index + 1,
      AlumniID: u.alumniID,
      Fullname: `${u.title || ""} ${u.firstName} ${u.lastName}`,
      Position: u.position,
      SystemRole: u.systemRole,
      Status: u.status
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

    XLSX.writeFile(workbook, "system_users.xlsx");

  };



  return (
    <div className="users-page">

      {/* USER HEADER */}
      <div className="users-user-card">

        <div>
          <div className="users-user-name">
            Welcome, {currentUser.fullname}
          </div>

          <div className="users-user-role">
            System Role: {currentUser.systemRole}
          </div>
        </div>

        <div className="users-time">
          <div>{formatDate(serverTime.fullDateTime)}</div>
          <div>{formatTime(serverTime.fullDateTime)}</div>
        </div>

      </div>



      <h1 className="users-title">System Users</h1>



      {/* ACTION BUTTONS */}
      <div className="users-actions">

        <button className="btn-primary" onClick={handleAddUser}>
          <UserPlus size={16}/> Add User
        </button>

        <button className="btn-secondary" onClick={handlePrint}>
          <Printer size={16}/> Print
        </button>

        <button className="btn-secondary" onClick={exportExcel}>
          <FileSpreadsheet size={16}/> Export Excel
        </button>

      </div>



      {/* USERS TABLE */}
      <div className="users-table-card">

        <table className="users-table">

          <thead>
            <tr>
              <th>SN</th>
              <th>AlumniID</th>
              <th>Fullname</th>
              <th>Position</th>
              <th>System Role</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>

            {users.map((u, index) => {

              const isProtected = u.systemRole === "SYSTEM ADMIN";

              return (

                <tr
                  key={u._id}
                  className={isProtected ? "protected-row" : ""}
                >

                  <td>{index + 1}</td>

                  <td>{u.alumniID}</td>

                  <td>{u.title} {u.firstName} {u.lastName}</td>

                  <td>
                    <select
                      value={u.position}
                      disabled={isProtected}
                      onChange={(e) =>
                        handlePositionChange(u._id, e.target.value)
                      }
                    >
                      <option value="MEMBER">MEMBER</option>
                      <option value="EXECUTIVE">EXECUTIVE</option>
                    </select>
                  </td>

                  <td>
                    <select
                      value={u.systemRole}
                      disabled={isProtected}
                      onChange={(e) =>
                        handleRoleChange(u._id, e.target.value)
                      }
                    >
                      <option value="MEMBER">MEMBER</option>
                      <option value="ADMIN">ADMIN</option>
                      <option value="SYSTEM ADMIN">SYSTEM ADMIN</option>
                    </select>
                  </td>

                  <td>
                    <select
                      value={u.status}
                      disabled={isProtected}
                      onChange={(e) =>
                        handleStatusChange(u._id, e.target.value)
                      }
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="INACTIVE">INACTIVE</option>
                    </select>
                  </td>

                  <td>
                    <button
                      className="reset-btn"
                      disabled={isProtected}
                      onClick={() => handleReset(u._id)}
                    >
                      <RotateCcw size={16}/> Reset
                    </button>
                  </td>

                </tr>

              );

            })}

          </tbody>

        </table>

      </div>

    </div>
  );
}